import { type Chat } from '@/contexts/chat/scripts/hooks/useChats/types';
import type { ChatMessage } from '@/contexts/chat/scripts/hooks/useChatMessages/types';
import {
  ChatMessagePayloadType,
  ChatMessagePulled,
  CustomChatMessagePayloadType,
} from '@/contexts/chat/scripts/hooks/useChatMessages/types';
import { safeJson } from '@/scripts/utils';
import CreateMessage from '../group/createMessage';
import GroupInviteMessage from '../group/groupInviteMessage';
import GroupRemoveMemberMessage from '../group/groupRemoveMemberMessage';
import MessageItem, { MessageStatusItem } from '../messageItem';
import SysText from '../sysText';
import DividerTime from '../dividerTime';
import DividerLine from '../dividerLine';
import GroupUpdateMessage from '../group/groupUpdateMessage';
import CallMessage from '../media/callMessage';
import DestroyExpireDuration from '../destoryExpireDuration';

interface MessageRendererProps {
  message: ChatMessage;
  chat: Chat;
  merge?: boolean;
  onClickRetry?: () => void;
}

const MessageRenderer = ({
  message,
  chat,
  merge,
  onClickRetry,
}: MessageRendererProps) => {
  switch (message.pulled) {
    case ChatMessagePulled.YES:
      switch (message.payload_type) {
        case CustomChatMessagePayloadType.DIVIDER_DATE:
          return <DividerLine>{message.payload}</DividerLine>;
        case CustomChatMessagePayloadType.ADD_TO_CONTACTS: {
          const { name } = safeJson(message.payload, {} as any);
          return (
            <SysText>
              You have added <a>{name}</a>, and now you can start chatting.
            </SysText>
          );
        }

        case CustomChatMessagePayloadType.DIVIDER_TIME:
          return <DividerTime>{message.payload}</DividerTime>;

        case ChatMessagePayloadType.TEXT:
        case ChatMessagePayloadType.ENCRYPTED_TEXT:
        case ChatMessagePayloadType.FILE:
        case ChatMessagePayloadType.IMAGE:
          return <MessageItem chat={chat} message={message} merge={merge} />;
        case CustomChatMessagePayloadType.DIVIDER_CREATE_CHAT:
          // case ChatMessagePayloadType.GROUP_CREATE:
          return <CreateMessage chat={chat} payload={message.payload} />;

        case ChatMessagePayloadType.GROUP_UPDATE:
          return <GroupUpdateMessage payload={message.payload} />;

        case ChatMessagePayloadType.GROUP_INVITE:
          return <GroupInviteMessage payload={message.payload} />;

        case ChatMessagePayloadType.GROUP_REMOVE_MEMBER:
          return <GroupRemoveMemberMessage payload={message.payload} />;

        case ChatMessagePayloadType.GROUP_DISBAND: {
          const { user_name } = safeJson(message.payload, {} as any);
          return (
            <SysText>The group owner {user_name} has disbanded this group</SysText>
          );
        }

        case ChatMessagePayloadType.GROUP_MEMBER_QUIT: {
          const { user_name } = safeJson(message.payload, {} as any);
          return <SysText>{user_name} has left the group</SysText>;
        }

        case ChatMessagePayloadType.MEDIA_AUDIO_CALL:
        case ChatMessagePayloadType.MEDIA_VIDEO_CALL:
          return (
            <CallMessage
              payload_type={message.payload_type}
              status={message.status}
              chat={chat}
              message={message}
            ></CallMessage>
          );

        case ChatMessagePayloadType.EXPIRE_DURATION:
          return <DestroyExpireDuration payload={message.payload} />;

        default:
          return null;
      }
    default:
      switch (message.payload_type) {
        case ChatMessagePayloadType.TEXT:
        case ChatMessagePayloadType.ENCRYPTED_TEXT:
          return (
            <MessageStatusItem
              chat={chat}
              message={message}
              merge={merge}
              onClickRetry={onClickRetry}
            />
          );
        default:
          return null;
      }
  }
};

export default memo(MessageRenderer);
